import { Container } from '@design-system/container';
import { SearchResultsHeader } from '@design-system/search-results-header';
import { type TabsProps, Tab, Tabs } from '@design-system/tabs';
import classNames from 'classnames';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';

import { gtmService } from '~/services/gtm';
import { isBrowser } from '~/utils/browser';
import getQueryParamByName from '~/utils/getQueryParamByName';
import { stripHtml } from '~/utils/strip-html';

import ArticleListing from './ArticleListing';
import EventListing from './EventListing';
import { SearchingState, SearchResultsProps, TabsVariant } from './models';
import ProductListing from './ProductListing';
import ResourceListing from './ResourceListing';
import { executeSearch } from './SearchService/executeSearch';
import { getPopularSearches } from './SearchService/getPopularSearches';
import { SearchResults as SearchResultsData } from './SearchService/types';
import SearchResultSkeleton from './Skeleton';

const SearchResults = forwardRef<HTMLDivElement, SearchResultsProps>(
  (
    {
      resultsTab,
      productTab,
      articleTab,
      resourceTab,
      eventTab,
      moreResultsButtonLabel,
      moreProductsButtonLabel,
      moreArticlesButtonLabel,
      moreEventsButtonLabel,
      moreResourcesButtonLabel,
      initialVisibleItems,
      header,
      imageSettings,
      fluidGrid,
      locale,
      uid,
      componentId,
    },
    ref
  ) => {
    const {
      mode,
      description,
      descriptionSize,
      phraseTag,
      phraseSize,
      hiddenHeadline,
      popularSearchesDescription,
      popularSearchesSize,
    } = header;

    const [searchResults, setSearchResults] = useState<SearchResultsData>({
      articles: [],
      products: [],
      resources: [],
      events: [],
    });
    const [popularSearches, setPopularSearches] =
      useState<Array<string> | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [searchingState, setSearchingState] = useState<SearchingState>(
      SearchingState.pending
    );

    const searchQuery =
      isBrowser() && stripHtml(decodeURI(getQueryParamByName('q') ?? ''));

    const handleTabChange = (newValue: number) => {
      setTabValue(newValue);
    };

    const secondaryTabIndexByVariant = useMemo(() => {
      let next = 1;
      const map: Partial<Record<TabsVariant, number>> = {};
      if (searchResults.products.length > 0) {
        map[TabsVariant.productsTab] = next;
        next += 1;
      }
      if (searchResults.articles.length > 0) {
        map[TabsVariant.articlesTab] = next;
        next += 1;
      }
      if (searchResults.events.length > 0) {
        map[TabsVariant.eventsTab] = next;
        next += 1;
      }
      if (searchResults.resources.length > 0) {
        map[TabsVariant.resourcesTab] = next;
        next += 1;
      }

      return map;
    }, [searchResults]);

    const maxTabIndex = useMemo(() => {
      const values = Object.values(secondaryTabIndexByVariant).filter(
        (v): v is number => typeof v === 'number'
      );

      return values.length ? Math.max(...values) : 0;
    }, [secondaryTabIndexByVariant]);

    useEffect(() => {
      if (searchingState !== SearchingState.results) {
        return;
      }
      if (tabValue > maxTabIndex) {
        setTabValue(0);
      }
    }, [maxTabIndex, searchingState, tabValue]);

    useEffect(() => {
      if (searchQuery) {
        gtmService.emitViewSearchResults(searchQuery);

        executeSearch(searchQuery, locale).then((searchResultsData) => {
          setSearchResults(searchResultsData);

          if (searchResults.products.length > 0) {
            gtmService.emitViewItemList(
              searchResults.products.map((product) => ({
                heroImage: {
                  url: product.image.url,
                  alt: product.image.title,
                  localAsset: { childImageSharp: product.image.data },
                },
                brandName: product.brandName,
                salsifyDigitalAssets: [],
                productTitle: product.title,
                descriptionShort: product.description,
                descriptionLong: '',
                ingredients: '',
                directionsForUse: '',
                safetyWarnings: '',
                netContentStatement: product.netContentStatement,
                aromaScent: product.aromaScent,
                variantAndIcon: product.variantAndIcon,
                Variant_And_Icon: product.variantAndIcon,
                heroImageID: '',
                websiteImagesID: [],
                barcodeNumber: product.barcodeNumber,
                sku: product.sku,
                segmentDescription: '',
                productFeatures: [],
                secondaryFeatures: [],
              }))
            );
          }

          if (
            searchResultsData.articles.length +
            searchResultsData.products.length +
            searchResultsData.resources.length +
            searchResultsData.events.length >
            0
          ) {
            setSearchingState(SearchingState.results);
          } else {
            setSearchingState(SearchingState.noResults);

            if (popularSearches == null) {
              getPopularSearches().then(
                (popularSearchesData) =>
                  popularSearchesData && setPopularSearches(popularSearchesData)
              );
            }
          }
        });
      } else {
        setSearchingState(SearchingState.emptyPhrase);
      }

      setIsLoading(false);
    }, [searchQuery, locale]);

    const getTabTitle = (tabName, resultCount, className = 'tab-title') => (
      <span className={className}>
        <span>{`${tabName}`}</span>
        &nbsp;
        <span>{`(${resultCount})`}</span>
      </span>
    );

    const secondaryDetailTabs = useMemo((): TabsProps['children'] => {
      const tabs: TabsProps['children'] = [];
      if (searchResults.products.length > 0) {
        tabs.push(
          <Tab
            key={productTab}
            title={getTabTitle(productTab, searchResults.products.length)}
          >
            <ProductListing
              items={searchResults.products}
              setTabValue={setTabValue}
              tabNumber={TabsVariant.allResults}
              moreButtonText={moreResultsButtonLabel}
              imageSettings={imageSettings}
              fluidGrid={fluidGrid}
            />
          </Tab>
        );
      }
      if (searchResults.articles.length > 0) {
        tabs.push(
          <Tab
            key={articleTab}
            title={getTabTitle(articleTab, searchResults.articles.length)}
          >
            <ArticleListing
              items={searchResults.articles}
              setTabValue={setTabValue}
              tabNumber={TabsVariant.allResults}
              moreButtonText={moreResultsButtonLabel}
              imageSettings={imageSettings}
              fluidGrid={fluidGrid}
            />
          </Tab>
        );
      }
      if (searchResults.events.length > 0) {
        tabs.push(
          <Tab
            key={eventTab}
            title={getTabTitle(eventTab, searchResults.events.length)}
          >
            <EventListing
              items={searchResults.events}
              setTabValue={setTabValue}
              tabNumber={TabsVariant.allResults}
              moreButtonText={moreResultsButtonLabel}
              imageSettings={imageSettings}
              fluidGrid={fluidGrid}
            />
          </Tab>
        );
      }
      if (searchResults.resources.length > 0) {
        tabs.push(
          <Tab
            key={resourceTab}
            title={getTabTitle(resourceTab, searchResults.resources.length)}
          >
            <ResourceListing
              items={searchResults.resources}
              setTabValue={setTabValue}
              tabNumber={TabsVariant.allResults}
              moreButtonText={moreResultsButtonLabel}
              imageSettings={imageSettings}
              fluidGrid={fluidGrid}
            />
          </Tab>
        );
      }

      return tabs;
    }, [
      articleTab,
      eventTab,
      fluidGrid,
      imageSettings,
      moreResultsButtonLabel,
      productTab,
      resourceTab,
      searchResults.articles,
      searchResults.events,
      searchResults.products,
      searchResults.resources,
    ]);

    return (
      <div id={componentId} data-component-uid={uid} ref={ref}>
        <SearchResultsHeader
          mode={mode}
          state={searchingState}
          description={description}
          descriptionSize={descriptionSize}
          phrase={searchQuery || undefined}
          phraseTag={phraseTag}
          phraseSize={phraseSize}
          hiddenHeadline={hiddenHeadline}
          popularSearches={popularSearches}
          popularSearchesDescription={popularSearchesDescription}
          popularSearchesSize={popularSearchesSize}
        />
        {searchingState === SearchingState.pending ? (
          <SearchResultSkeleton initialVisibleItems={initialVisibleItems} />
        ) : null}
        {searchingState === SearchingState.results ? (
          <Container
            className={classNames('search-result', {
              'search-result--loaded': !isLoading,
            })}
            maxWidth="max-grid"
          >
            <Tabs value={tabValue} onChange={handleTabChange}>
              {[
                <Tab
                  key={resultsTab}
                  title={getTabTitle(
                    resultsTab,
                    searchResults.articles.length +
                    searchResults.products.length +
                    searchResults.events.length +
                    searchResults.resources.length
                  )}
                >
                  {searchResults.products.length > 0 ? (
                    <ProductListing
                      tabItemsTitle={getTabTitle(
                        productTab,
                        searchResults.products.length,
                        'tab-content-title'
                      )}
                      items={searchResults.products}
                      maxVisibleItems={initialVisibleItems}
                      setTabValue={setTabValue}
                      tabNumber={
                        secondaryTabIndexByVariant[TabsVariant.productsTab] ?? 0
                      }
                      moreButtonText={moreProductsButtonLabel}
                      imageSettings={imageSettings}
                      fluidGrid={fluidGrid}
                    />
                  ) : null}

                  {searchResults.articles.length > 0 ? (
                    <ArticleListing
                      tabItemsTitle={getTabTitle(
                        articleTab,
                        searchResults.articles.length,
                        'tab-content-title'
                      )}
                      items={searchResults.articles}
                      maxVisibleItems={initialVisibleItems}
                      setTabValue={setTabValue}
                      tabNumber={
                        secondaryTabIndexByVariant[TabsVariant.articlesTab] ?? 0
                      }
                      moreButtonText={moreArticlesButtonLabel}
                      imageSettings={imageSettings}
                      fluidGrid={fluidGrid}
                    />
                  ) : null}

                  {searchResults.events.length > 0 ? (
                    <EventListing
                      items={searchResults.events}
                      tabItemsTitle={getTabTitle(
                        eventTab,
                        searchResults.events.length,
                        'tab-content-title'
                      )}
                      maxVisibleItems={initialVisibleItems}
                      setTabValue={setTabValue}
                      tabNumber={
                        secondaryTabIndexByVariant[TabsVariant.eventsTab] ?? 0
                      }
                      moreButtonText={moreEventsButtonLabel}
                      imageSettings={imageSettings}
                      fluidGrid={fluidGrid}
                    />
                  ) : null}

                  {searchResults.resources.length > 0 ? (
                    <ResourceListing
                      items={searchResults.resources}
                      tabItemsTitle={getTabTitle(
                        resourceTab,
                        searchResults.resources.length,
                        'tab-content-title'
                      )}
                      maxVisibleItems={initialVisibleItems}
                      setTabValue={setTabValue}
                      tabNumber={
                        secondaryTabIndexByVariant[TabsVariant.resourcesTab] ?? 0
                      }
                      moreButtonText={moreResourcesButtonLabel}
                      imageSettings={imageSettings}
                      fluidGrid={fluidGrid}
                    />
                  ) : null}
                </Tab>,
                ...secondaryDetailTabs,
              ]}
            </Tabs>
          </Container>
        ) : null}
      </div>
    );
  }
);

export default SearchResults;
